
import Vue from 'vue';
import { Runnit, RunnitNode, RunnitNodeRunResult } from '@run-diffusion/shared';
import { TemplateGenerationResultsMixin, TEMPLATE_MAX_RESULTS_PER_NODE } from '@/mixins';
import NodeNavigationPanel from './NodeNavigationPanel.vue';
import AssignedGenerationsSection from './AssignedGenerationsSection.vue';
import AvailableGenerationsSection from './AvailableGenerationsSection.vue';

export default Vue.extend({
	name: 'TemplateGenerationSelector',
	mixins: [TemplateGenerationResultsMixin],
	components: {
		NodeNavigationPanel,
		AssignedGenerationsSection,
		AvailableGenerationsSection,
	},
	props: {
		selectedNodes: {
			type: Array as () => RunnitNode[],
			required: true,
		},
		sourceRunnit: {
			type: Object as () => Runnit | null,
			default: null,
		},
		selectedResults: {
			type: Array as () => Array<{
				templateNodeId: string
				sourceNodeRunId: string
				resultUuid: string
				result: RunnitNodeRunResult
			}>,
			default: () => [],
		},
	},
	data () {
		return {
			selectedNodeId: null as string | null,
			prefillingAllTools: false,
		};
	},
	computed: {
		selectedNode (): RunnitNode | null {
			if (!this.selectedNodeId || !this.selectedNodes.length) return null;
			return this.selectedNodes.find(node => node.id === this.selectedNodeId) || null;
		},
	},
	methods: {
		onNodeSelected (nodeId: string) {
			this.selectedNodeId = nodeId;
		},
		onResultSelected (result: {
			templateNodeId: string
			sourceNodeRunId: string
			resultUuid: string
			result: RunnitNodeRunResult
		}) {
			const newResults = [...this.selectedResults, result];
			this.$emit('results-selected', newResults);
		},
		onResultsSelectedBulk (results: Array<{
			templateNodeId: string
			sourceNodeRunId: string
			resultUuid: string
			result: RunnitNodeRunResult
		}>) {
			// Merge all new results with existing ones, avoiding duplicates
			const existingResultKeys = new Set(
				this.selectedResults.map(r => `${r.templateNodeId}-${r.resultUuid}`)
			);
			const newResultsToAdd = results.filter(
				r => !existingResultKeys.has(`${r.templateNodeId}-${r.resultUuid}`)
			);
			const newResults = [...this.selectedResults, ...newResultsToAdd];
			this.$emit('results-selected', newResults);
		},
		onResultUnselected (resultUuid: string, templateNodeId: string) {
			const newResults = this.selectedResults.filter(
				r => !(r.resultUuid === resultUuid && r.templateNodeId === templateNodeId)
			);
			this.$emit('results-selected', newResults);
		},
		onClearAllForNode (templateNodeId: string) {
			const newResults = this.selectedResults.filter(
				r => r.templateNodeId !== templateNodeId
			);
			this.$emit('results-selected', newResults);
		},
		async onPrefillAllTools () {
			if (!this.selectedNodes || this.selectedNodes.length === 0) return;

			this.prefillingAllTools = true;
			try {
				const allResultsToSelect: Array<{
					templateNodeId: string
					sourceNodeRunId: string
					resultUuid: string
					result: RunnitNodeRunResult
				}> = [];

				// Process each node
				for (const node of this.selectedNodes) {
					// Get currently selected count for this node
					const currentCount = this.selectedResults.filter(r => r.templateNodeId === node.id).length;
					if (currentCount >= TEMPLATE_MAX_RESULTS_PER_NODE) continue; // Already at limit

					// Load available results for this node
					const availableResultsResponse = await this.loadAvailableResultsForNode(node, this.sourceRunnit);
					const availableResults = availableResultsResponse.results;

					// Filter out already selected results
					const existingResultUuids = new Set(
						this.selectedResults
							.filter(r => r.templateNodeId === node.id)
							.map(r => r.resultUuid)
					);
					const unselectedResults = availableResults.filter(
						resultItem => !existingResultUuids.has(resultItem.result.uuid)
					);

					// Select up to TEMPLATE_MAX_RESULTS_PER_NODE most recent results (availableResults is already sorted by initAt desc)
					const remainingSlots = TEMPLATE_MAX_RESULTS_PER_NODE - currentCount;
					const toSelect = unselectedResults.slice(0, remainingSlots);

					// Add to bulk selection
					for (const resultItem of toSelect) {
						allResultsToSelect.push({
							templateNodeId: node.id,
							sourceNodeRunId: resultItem.nodeRunId,
							resultUuid: resultItem.result.uuid,
							result: resultItem.result,
						});
					}
				}

				// Emit bulk selection if we have any results
				if (allResultsToSelect.length > 0) {
					this.onResultsSelectedBulk(allResultsToSelect);
				}
			} catch (error) {
				console.error('Error prefilling all tools:', error);
			} finally {
				this.prefillingAllTools = false;
			}
		},
	},
	mounted () {
		// Auto-select first node if available
		if (this.selectedNodes.length > 0 && !this.selectedNodeId) {
			this.selectedNodeId = this.selectedNodes[0].id;
		}
	},
	watch: {
		selectedNodes: {
			immediate: true,
			handler (newNodes: RunnitNode[]) {
				// Auto-select first node if current selection is invalid
				if (newNodes.length > 0 && (!this.selectedNodeId || !newNodes.find(n => n.id === this.selectedNodeId))) {
					this.selectedNodeId = newNodes[0].id;
				}
			},
		},
	},
});
